import type { Turn } from '@chessmate/protocol';
import type { BackgammonResult } from './backgammon.js';

/**
 * The doubling cube: pure algebra, no adapter, no protocol.
 *
 * Online-only for now (operator-ratified 2026-10-05): the offline robot has
 * no cube policy, so nothing here decides *when* to double — that judgement
 * stays with the players. This module only answers the state questions the
 * API and the store both need, so "who may offer" and "what does a take
 * change" cannot drift between the server's replay validation and the UI's
 * button gating.
 *
 * Rules encoded (standard money-game, simplified v1): the cube starts
 * centered at 1 owned by nobody; the owner (or either side while centered)
 * may offer a double instead of rolling; a take doubles the value and hands
 * ownership — and the next roll — to the taker; a pass pays the *current*
 * value, so the offer's value is only ever reached on a take.
 */

/** The cube's value caps here; a doubled 64 is played out at 64. */
export const CUBE_MAX = 64;

/** Cube ownership: `'c'` is the centered cube nobody owns yet. */
export type CubeOwner = Turn | 'c';

export interface BackgammonCube {
  /** Stake multiplier, 1..64, powers of two only. */
  readonly value: number;
  readonly owner: CubeOwner;
}

/** A double awaiting take/pass. While one is pending the doubler is still
 *  the side to move — answering is the opponent's job. */
export interface BackgammonCubeOffer {
  readonly by: Turn;
  /** The value if taken (`cube.value * 2`, capped). */
  readonly value: number;
}

/** Snapshots from before the cube existed lack the field; every reader
 *  normalizes through this instead of assuming presence. */
export function cubeOf(cube: BackgammonCube | undefined | null): BackgammonCube {
  return cube ?? { value: 1, owner: 'c' };
}

/** May `side` offer a double right now? Owner-only once claimed, never past
 *  64, never on top of a pending offer. */
export function canOfferDouble(
  cube: BackgammonCube,
  pending: BackgammonCubeOffer | null | undefined,
  side: Turn,
): boolean {
  if (pending) return false;
  if (cube.owner !== 'c' && cube.owner !== side) return false;
  return cube.value < CUBE_MAX;
}

/** The value the opponent sees on the offer slip. */
export function offeredValue(cube: BackgammonCube): number {
  return Math.min(cube.value * 2, CUBE_MAX);
}

/** A take: value doubles, the taker owns the cube, and — the part the API's
 *  turn guard then enforces for free — the taker plays next. */
export function takeCube(
  offer: BackgammonCubeOffer,
  taker: Turn,
): { cube: BackgammonCube; turn: Turn } {
  return { cube: { value: offer.value, owner: taker }, turn: taker };
}

/** Stake multiplier by result kind: plain 1, gammon 2, backgammon 3 —
 *  multiplied by the cube value for the final points. */
export function stakeMultiplier(kind: BackgammonResult['kind']): 1 | 2 | 3 {
  switch (kind) {
    case 'elimination':
      return 1;
    case 'gammon':
      return 2;
    case 'backgammon':
      return 3;
  }
}
